
    import __i18nConfig from '@next-translate-root/i18n'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    import { GetStaticProps, NextPage } from 'next';
import { useRouter } from 'next/router';
import { unstable_serialize } from 'swr';
import { unstable_serialize as infSerialize } from 'swr/infinite';
import useTranslation from 'next-translate/useTranslation';
import { NextSeo } from 'next-seo';
import { captureException } from '@sentry/nextjs';
import { useApi } from '../../services/useApi';
import { parseResponse } from '../../services/useApiHelpers';
import PageWithFallback from '../../components/Catalog/WithFallback';
import Suppliers from '../../components/Catalog/SupplierCatalog/Suppliers';
import PageWithInstantSearch from '../../components/Catalog/WithInstantSearch';
import fetcher from '../../utils/fetcher';
import { getMainHeading } from '../../lib/catalog';
import { getSEOProps } from '../../next-seo.config';
import { QueryParam, ResourceTag, ScreenSize, SortOption } from '../../utils/constants';
import { WithFallback } from '../../utils/types/misc';
import { Catalog, Supplier } from '../../utils/types/Product';
import { CountryAndLocale } from '../../external';
import { getLocalizedCatalogUrls } from '../../utils/urls';
import { createUrlParams } from '../../lib/params';
import RfqBanner from '../../components/Catalog/ProductCatalog/RfqBanner';
import Newsletter from '../../components/LandingPage/Newsletter';
import CategoryBreadcrumb from '../../components/Catalog/CategoryBreadcrumb';
import { getCountryAndLocaleStrings, getCurrencyFromCountryAndLocale } from '../../utils/locales';
import useIsOnMobile from '../../services/useIsOnMobile';
import useEmptyStateEvent from '../../services/analytics/useEmptyStateEvent';
import styles from '../../styles/SupplierCatalog.module.less';
interface SupplierCatalogProps {
    localizedUrls?: Record<CountryAndLocale, string>;
}
const DEFAULT_SORT_QUERY = 'supplier.address.position';
let sortParam = DEFAULT_SORT_QUERY;
const SupplierCatalog: NextPage<SupplierCatalogProps> = (props) => {
    const { localizedUrls, } = props;
    const { t, lang: countryAndLang } = useTranslation('products');
    const { query, asPath } = useRouter();
    const { getResource } = useApi();
    const queryParams = asPath.split('?')[1];
    const isOnMobile = useIsOnMobile(ScreenSize.md);
    const { locale: lang } = getCountryAndLocaleStrings(countryAndLang);
    const sortQuery = query[QueryParam.sort];
    switch (sortQuery) {
        case SortOption.newest:
            sortParam = '-supplier.ctime';
            break;
        case SortOption.name_az:
            sortParam = 'supplier.label';
            break;
        case SortOption.name_za:
            sortParam = '-supplier.label';
            break;
        default:
            sortParam = DEFAULT_SORT_QUERY;
            break;
    }
    const supplierQuery = createUrlParams({
        include: ['media', 'text', 'supplier/address', 'supplier/service', 'service/price'],
        sort: sortParam,
        page: {
            limit: '24',
        },
    });
    const { data: suppliersList, isLoading, total, } = getResource<Supplier>(ResourceTag.supplier, supplierQuery);
    const { data: categories } = getResource<Catalog>('catalog', 'include=catalog,media,text');
    const categoryBreadcrumbsItems = [{ id: '', code: 'suppliers', label: 'Suppliers' }];
    // Analytics event
    useEmptyStateEvent(suppliersList.length, isLoading, 'supplier_catalog', // Analytics context
    [queryParams]);
    // SEO props
    const seoTitle = getMainHeading('suppliers', query, t, lang, categories);
    const canonicalUrl = localizedUrls?.[countryAndLang as CountryAndLocale];
    return (<>
      <NextSeo {...getSEOProps()({
        title: seoTitle,
        path: asPath,
        hreflangUrls: localizedUrls,
        canonicalUrl,
    })}/>
      <div className="max-width-lg margin-bottom-spacer-double">
        <div className={`${styles.suppliers} max-width-lg margin-bottom-spacer`}>
          <div className={isOnMobile ? '' : 'padding-left-spacer'}>
            <CategoryBreadcrumb breadcrumbs={categoryBreadcrumbsItems} category="suppliers"/>
          </div>
          <Suppliers suppliers={suppliersList} isLoading={isLoading || suppliersList.length < total} totalSuppliersAmount={total}/>
          <RfqBanner forCatalog/>
          <div className="margin-top-spacer-quadruple">
            <Newsletter />
          </div>
        </div>
      </div>
    </>);
};
export default PageWithFallback(PageWithInstantSearch(SupplierCatalog));
const getStaticProps: GetStaticProps = async ({ locale: countryAndLocale, defaultLocale: defaultCountryAndLocale, params, }) => {
    const url = process.env.NEXT_PUBLIC_API_URL;
    const { locale, country, } = getCountryAndLocaleStrings((countryAndLocale || defaultCountryAndLocale!));
    const localizedUrls = await getLocalizedCatalogUrls('suppliers', params); // SEO urls
    const res: {
        props: WithFallback<SupplierCatalogProps>;
        revalidate: number;
    } = {
        props: { fallback: {}, localizedUrls },
        revalidate: Number(process.env.REGENERATE_INTERVAL) || 60,
    };
    const currency = getCurrencyFromCountryAndLocale(countryAndLocale as CountryAndLocale);
    const optionsUrl = `${url}/jsonapi/default?currency=${currency}&locale=${locale}`;
    const supplierUrl = `${url}/jsonapi/default/supplier?currency=${currency}&locale=${locale}&include=media,text,supplier/address,supplier/service,service/price&sort=supplier.address.position&page[limit]=24`;
    const catalogUrl = `${url}/jsonapi/default/catalog?currency=${currency}&locale=${locale}&include=catalog,media,text`;
    try {
        const [optionsResp, categoriesData, supplierResp,] = await Promise.all([
            fetcher(optionsUrl, 'OPTIONS').then((resp) => resp.json()),
            fetcher(catalogUrl).then((resp) => resp.json()),
            fetcher(supplierUrl).then((resp) => resp.json()),
        ]);
        const query = `?currency=${currency}&locale=${locale}&country=${country}&include=attribute,media,price,product,product/property,text,catalog,supplier,stock`;
        const productUrl = `${url}/jsonapi/default/product${query}`;
        const { data: supplierData } = parseResponse<Supplier>(supplierResp);
        const productUrls = supplierData.map((supplier) => `${productUrl}&filter[f_supid]=${supplier.id}&page[limit]=12`);
        const productsPerSupplier: [
            string,
            any
        ][] = await Promise.all(productUrls.map(async (prodUrl) => ([infSerialize(() => prodUrl), await fetcher(prodUrl).then((resp) => resp.json())])));
        const fetchParams = { method: 'OPTIONS' };
        res.props = {
            fallback: {
                [unstable_serialize([optionsUrl, JSON.stringify(fetchParams)])]: optionsResp,
                [infSerialize(() => supplierUrl)]: supplierResp,
                [infSerialize(() => catalogUrl)]: categoriesData,
                ...Object.fromEntries(productsPerSupplier),
                localizedUrls,
            },
            localizedUrls,
        };
    }
    catch (e) {
        captureException(e);
        console.error('Could not generate suppliers catalogue - ', e);
    }
    return res;
};

    async function __Next_Translate__getStaticProps__196fde50ade__(ctx) {
      const res = await getStaticProps(ctx)
      return {
        
        ...res,
        props: {
          ...(res.props || {}),
          ...(await __loadNamespaces({
            ...ctx,
            ...__i18nConfig,
            pathname: '/suppliers/index',
            loaderName: 'getStaticProps',
            loadLocaleFrom: __i18nConfig && __i18nConfig.loadLocaleFrom || ((l, n) => import(`@next-translate-root/locales/${l}/${n}`).then(m => m.default)),
          }))
        }
      }
    }
    export { __Next_Translate__getStaticProps__196fde50ade__ as getStaticProps }
  